import { EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { PatientDTO } from "@metriport/api-sdk";
import { useEffect } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiInfo } from "react-icons/fi";
import { PatientTab } from "../shared-logic/consolidated-context/reducer";
import { useRefAndUpdateHeight } from "../shared-logic/elementHeight";
import { useSharedContext } from "../shared-logic/shared-context";
import { METRIPORT_PRIMARY } from "../shared-logic/style";
import { Button as MetriportButton } from "./button";
import { IdContainer } from "./id-copy-container";

type HeaderActions = {
  onQueryPatientDocuments: () => Promise<void>;
  onGenerateMedicalRecord: () => void;
  onEditPatient: () => void;
  onDisplayPatientDemos: () => void;
  onViewPatientMatches: () => void;
  onSetPatientTab: (tab: PatientTab) => void;
  toggleHieOptOut: (hieOptOut: boolean) => Promise<void>;
  toggleUsingSectionSearch: () => void;
  onEditCustomKeywords?: () => void;
  setUsingMetriportOnlyBundleFromConsolidated?: (
    usingMetriportOnlyBundleFromConsolidated: boolean
  ) => void;
};

type HeaderParams = {
  patient: PatientDTO | undefined;
  isFetchingPatient: boolean;
  isQueryingDocuments: boolean;
  isFetchingDiff: boolean;
  isAtLeastOneMrDownloading: boolean;
  isHieOptedOut: boolean;
  usingSectionSearch: boolean;
  usingMetriportOnlyBundleFromConsolidated: boolean;
  selectedTab: PatientTab;
  lastDocQuery: string | undefined;
  isLimited?: boolean;
  actions: HeaderActions;
  setHeaderHeight: (height: number) => void;
};

export function PatientHeader({
  patient,
  isFetchingPatient,
  isQueryingDocuments,
  isFetchingDiff,
  isAtLeastOneMrDownloading,
  isHieOptedOut,
  usingSectionSearch,
  usingMetriportOnlyBundleFromConsolidated,
  lastDocQuery,
  isLimited,
  actions,
  setHeaderHeight,
}: HeaderParams) {
  const { elementRef, updateHeight } = useRefAndUpdateHeight(setHeaderHeight);
  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  return (
    <Box ref={elementRef} bg={useColorModeValue("gray.100", "gray.900")}>
      {!isLimited && lastDocQuery && <LastQueriedFull lastDocQuery={lastDocQuery} />}
      <Box
        bg={useColorModeValue("white", "gray.800")}
        border={"solid"}
        borderRadius={10}
        borderColor={useColorModeValue("gray.200", "gray.700")}
        px={5}
        py={isLimited ? 2 : 3}
      >
        <Flex flexDirection={"row"} justifyContent={"space-between"}>
          <Flex
            flexDirection={{ base: "column", md: isLimited ? "row" : "column" }}
            justifyContent={"flex-start"}
          >
            <Flex>
              <PatientInfo
                isLimited={isLimited}
                patient={patient}
                isLoading={isFetchingPatient}
                actions={isLimited ? undefined : actions}
                lastDocQuery={isLimited ? lastDocQuery : undefined}
              />
            </Flex>
            <Flex ml={{ base: 0, md: isLimited ? 5 : 0 }}>
              <PatientDemo
                isLimited={isLimited}
                patient={patient}
                isLoading={isFetchingPatient}
                actions={actions}
                lastDocQuery={isLimited ? lastDocQuery : undefined}
              />
            </Flex>
          </Flex>
          <Flex
            flexDirection={{ base: "column", md: isLimited ? "row" : "column", xl: "row" }}
            justifyContent={"flex-end"}
          >
            <Flex mt={1}>
              {isLimited ? (
                <Flex flexDirection={"row"}>
                  {actions.setUsingMetriportOnlyBundleFromConsolidated && (
                    <Box display={{ base: "none", md: "block" }}>
                      <EhrDiffButton
                        isFetchingDiff={isFetchingDiff}
                        isQueryingDocuments={isQueryingDocuments}
                        usingMetriportOnlyBundleFromConsolidated={
                          usingMetriportOnlyBundleFromConsolidated
                        }
                        actions={actions}
                      />
                    </Box>
                  )}
                </Flex>
              ) : (
                <PatientButtonActions
                  isAtLeastOneMrDownloading={isAtLeastOneMrDownloading}
                  isQueryingDocuments={isQueryingDocuments}
                  actions={actions}
                />
              )}
            </Flex>
            <Flex
              mt={{ base: 2, md: isLimited ? 1 : 2, xl: 1 }}
              ml={{ base: "auto", md: isLimited ? 5 : "auto", xl: 5 }}
            >
              {isLimited ? (
                <Flex flexDirection={"row"}>
                  {actions.setUsingMetriportOnlyBundleFromConsolidated && (
                    <Box display={{ base: "block", md: "none" }}>
                      <EhrDiffButton
                        isFetchingDiff={isFetchingDiff}
                        isQueryingDocuments={isQueryingDocuments}
                        usingMetriportOnlyBundleFromConsolidated={
                          usingMetriportOnlyBundleFromConsolidated
                        }
                        actions={actions}
                      />
                    </Box>
                  )}
                  <PatientMenuActionsLimited
                    usingSectionSearch={usingSectionSearch}
                    actions={actions}
                    isQueryingDocuments={isQueryingDocuments}
                  />
                </Flex>
              ) : (
                <PatientMenuActionsFull
                  usingSectionSearch={usingSectionSearch}
                  isHieOptedOut={isHieOptedOut}
                  actions={actions}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

function LastQueriedFull({ lastDocQuery }: { lastDocQuery: string | undefined }) {
  return (
    <Box
      display={{ base: "none", "2xl": "block" }}
      position={"absolute"}
      top={0}
      marginLeft={"auto"}
      marginRight={"auto"}
      left={0}
      right={0}
      border={"solid"}
      borderTop={0}
      borderBottomRadius={10}
      borderColor={METRIPORT_PRIMARY}
      width={"fit-content"}
      paddingX={4}
      paddingY={2}
      fontSize={"sm"}
    >
      <Text color={METRIPORT_PRIMARY}>Refreshed: {lastDocQuery ?? "-"}</Text>
    </Box>
  );
}

function EhrDiffButton({
  isFetchingDiff,
  isQueryingDocuments,
  usingMetriportOnlyBundleFromConsolidated,
  actions,
}: {
  isFetchingDiff: boolean;
  isQueryingDocuments: boolean;
  usingMetriportOnlyBundleFromConsolidated: boolean;
  actions: Pick<HeaderActions, "setUsingMetriportOnlyBundleFromConsolidated">;
}) {
  return (
    <Flex ml={5} alignItems={"center"} mr={{ base: 2, md: 0 }}>
      <Tooltip label="Show data that is only present in Metriport and not in your EHR">
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="delta" fontSize="xs" mb={0} mr={1} fontWeight="semibold">
            Delta
          </FormLabel>
          <Switch
            size="sm"
            isChecked={usingMetriportOnlyBundleFromConsolidated}
            onChange={() => {
              actions.setUsingMetriportOnlyBundleFromConsolidated?.(
                !usingMetriportOnlyBundleFromConsolidated
              );
            }}
            colorScheme={"blue"}
            isDisabled={isFetchingDiff || isQueryingDocuments}
          />
        </FormControl>
      </Tooltip>
    </Flex>
  );
}

function HighRiskRenderer(isHighRisk: boolean | undefined) {
  return isHighRisk ? <HighRiskBadge /> : null;
}

function PatientInfo({
  isLimited = false,
  patient,
  isLoading,
  actions,
  lastDocQuery,
}: {
  isLimited?: boolean;
  patient: PatientDTO | undefined;
  isLoading: boolean;
  actions?: Pick<HeaderActions, "onEditPatient">;
  lastDocQuery?: string | undefined;
}) {
  const { sharedState } = useSharedContext();
  const patientName = `${patient?.firstName} ${patient?.lastName}`;
  return (
    <Skeleton display={"flex"} flexDirection={"row"} isLoaded={!isLoading}>
      <Flex alignItems="center">
        <Text fontSize={isLimited ? "xs" : "2xl"} fontWeight={"bold"}>
          {patientName}
        </Text>
        {HighRiskRenderer(sharedState.isMetriportUser)}
      </Flex>
      {lastDocQuery && (
        <Box display={{ base: "block", md: "none" }} fontSize={"xs"} ml={2}>
          <Text>Refreshed: {lastDocQuery}</Text>
        </Box>
      )}
      {actions && (
        <IconButton
          onClick={actions.onEditPatient}
          bg={"transparent"}
          _hover={{ color: "#879ced" }}
          aria-label="Edit Patient"
          icon={<EditIcon />}
        />
      )}
    </Skeleton>
  );
}

function PatientDemo({
  isLimited = false,
  patient,
  lastDocQuery,
  isLoading,
  actions,
}: {
  isLimited?: boolean;
  patient: PatientDTO | undefined;
  lastDocQuery?: string | undefined;
  isLoading: boolean;
  actions: Pick<HeaderActions, "onDisplayPatientDemos">;
}) {
  const demoFields: Array<{ label: string; value: string | undefined }> = [
    { label: "id", value: patient?.id },
    { label: "dob", value: patient?.dob },
    { label: "gender", value: patient?.genderAtBirth },
  ];
  const borderColor = useColorModeValue("gray.300", "gray.600");

  return (
    <Skeleton display={"flex"} flexDirection={"row"} isLoaded={!isLoading}>
      {demoFields.map(({ label, value }, i) => (
        <Flex
          key={i}
          pl={i === 0 ? 0 : 2}
          pr={2}
          borderRight={"solid"}
          borderColor={borderColor}
          whiteSpace={"nowrap"}
        >
          <Box>
            {label === "id" ? (
              <IdContainer fontSize={isLimited ? "xs" : "md"} value={value} entity={"Patient"} />
            ) : (
              <Text fontSize={isLimited ? "xs" : "md"}>{value}</Text>
            )}
          </Box>
        </Flex>
      ))}
      <Icon
        pl={2}
        onClick={actions.onDisplayPatientDemos}
        bg={"transparent"}
        _hover={{ color: "#879ced" }}
        cursor={"pointer"}
        aria-label="Patient Demographics"
        as={FiInfo}
        fontSize={isLimited ? "xl" : "2xl"}
      />
      {lastDocQuery && (
        <Box display={{ base: "none", md: "block" }} fontSize={"xs"} ml={2}>
          <Text>Refreshed: {lastDocQuery}</Text>
        </Box>
      )}
    </Skeleton>
  );
}

function PatientButtonActions({
  isAtLeastOneMrDownloading,
  isQueryingDocuments,
  actions,
}: {
  isAtLeastOneMrDownloading: boolean;
  isQueryingDocuments: boolean;
  actions: Pick<HeaderActions, "onGenerateMedicalRecord" | "onQueryPatientDocuments">;
}) {
  return (
    <Flex justifyContent={"center"}>
      <MetriportButton
        mr={5}
        size={"md"}
        isLoading={isAtLeastOneMrDownloading}
        onClick={actions.onGenerateMedicalRecord}
      >
        Generate Medical Record
      </MetriportButton>
      <MetriportButton
        size={"md"}
        isLoading={isQueryingDocuments}
        onClick={actions.onQueryPatientDocuments}
        variant={"outline"}
      >
        Refresh Patient Data
      </MetriportButton>
    </Flex>
  );
}

function PatientMenuActionsFull({
  usingSectionSearch,
  isHieOptedOut,
  actions,
}: {
  isHieOptedOut?: boolean;
  usingSectionSearch: boolean;
  actions: Pick<
    HeaderActions,
    | "onViewPatientMatches"
    | "onSetPatientTab"
    | "toggleHieOptOut"
    | "toggleUsingSectionSearch"
    | "onEditCustomKeywords"
  >;
}) {
  const bg = useColorModeValue("gray.50", "gray.800");
  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label="Options"
        icon={<BsThreeDotsVertical />}
        variant="outline"
      />
      <MenuList bg={bg}>
        <MenuItem bg={bg} onClick={() => actions.toggleUsingSectionSearch()}>
          {usingSectionSearch ? "Disable " : "Enable "} Search Per Section
        </MenuItem>
        {actions.onEditCustomKeywords && (
          <MenuItem bg={bg} onClick={actions.onEditCustomKeywords}>
            Edit Custom Keywords
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            actions.onSetPatientTab("matches");
            actions.onViewPatientMatches();
          }}
          bg={bg}
        >
          View Patient Matches
        </MenuItem>
        <MenuItem onClick={() => actions.toggleHieOptOut(!isHieOptedOut)} bg={bg}>
          {isHieOptedOut ? "Opt In to" : "Opt Out"} of Networks
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

function PatientMenuActionsLimited({
  usingSectionSearch,
  actions,
  isQueryingDocuments,
}: {
  usingSectionSearch: boolean;
  actions: Pick<
    HeaderActions,
    | "onGenerateMedicalRecord"
    | "onQueryPatientDocuments"
    | "toggleUsingSectionSearch"
    | "onSetPatientTab"
    | "onViewPatientMatches"
    | "onEditCustomKeywords"
  >;
  isQueryingDocuments: boolean;
}) {
  const bg = useColorModeValue("gray.50", "gray.800");
  return (
    <Menu placement="bottom-end">
      <MenuButton
        size={"xs"}
        as={IconButton}
        aria-label="Options"
        icon={<BsThreeDotsVertical />}
        variant="outline"
      />
      <MenuList bg={bg}>
        <MenuItem bg={bg} onClick={() => actions.toggleUsingSectionSearch()}>
          {usingSectionSearch ? "Disable " : "Enable "} Search Per Section
        </MenuItem>
        {actions.onEditCustomKeywords && (
          <MenuItem bg={bg} onClick={actions.onEditCustomKeywords}>
            Edit Custom Keywords
          </MenuItem>
        )}
        <MenuItem
          isDisabled={isQueryingDocuments}
          onClick={actions.onQueryPatientDocuments}
          bg={bg}
        >
          Refresh Patient Data
        </MenuItem>
        <MenuItem
          onClick={() => {
            actions.onSetPatientTab("matches");
            actions.onViewPatientMatches();
          }}
          bg={bg}
        >
          View Patient Matches
        </MenuItem>
        <MenuItem onClick={actions.onGenerateMedicalRecord} bg={bg}>
          Generate Medical Record
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

export function HighRiskBadge() {
  return (
    <Box
      as="span"
      px="2"
      py="0.5"
      height="1.25rem"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      fontSize="2xs"
      fontWeight="900"
      textTransform="uppercase"
      letterSpacing="0.05em"
      color="white"
      bg={"red"}
      m="2"
      transition="all 0.2s ease"
    >
      High Risk
    </Box>
  );
}
